import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import axios from 'axios';
import { API_URL } from '../../config/apiConfig';
import { FaSave, FaSpinner } from 'react-icons/fa';

const CancellationPolicy = () => {
  const [policy, setPolicy] = useState({
    title: '',
    content: '',
    sections: []
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newSection, setNewSection] = useState({ heading: '', description: '' });

  useEffect(() => {
    fetchPolicy();
  }, []);

  const fetchPolicy = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/content/public/cancellation-policy`);
      if (response.data.success) {
        setPolicy(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching policy:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setPolicy((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const addSection = () => {
    if (!newSection.heading || !newSection.description) {
      toast.error('Please fill in both heading and description');
      return;
    }
    setPolicy((prev) => ({
      ...prev,
      sections: [...prev.sections, newSection]
    }));
    setNewSection({ heading: '', description: '' });
  };

  const removeSection = (index) => {
    setPolicy((prev) => ({
      ...prev,
      sections: prev.sections.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!policy.title || !policy.content) {
      toast.error('Title and content are required');
      return;
    }

    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API_URL}/content/admin/cancellation-policy`,
        policy,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        toast.success('Cancellation policy updated successfully');
        setPolicy(response.data.data);
      }
    } catch (error) {
      console.error('Error updating policy:', error);
      toast.error(error.response?.data?.message || 'Failed to update policy');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex justify-center items-center min-h-96">
        <FaSpinner className="animate-spin text-3xl text-blue-600" />
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">Cancellation Policy</h1>
        <p className="text-gray-600">Manage your cancellation policy content</p>
      </div>

      <div className="bg-white rounded-lg shadow-lg p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Policy Title
            </label>
            <input
              type="text"
              value={policy.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              placeholder="e.g., Free Cancellation Policy"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Content */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Policy Content
            </label>
            <textarea
              value={policy.content}
              onChange={(e) => handleInputChange('content', e.target.value)}
              placeholder="Enter detailed cancellation policy content..."
              rows="8"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
            />
          </div>

          {/* Sections */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Policy Sections</h3>

            {/* Add New Section */}
            <div className="bg-gray-50 p-4 rounded-lg mb-4 space-y-3">
              <input
                type="text"
                value={newSection.heading}
                onChange={(e) => setNewSection({ ...newSection, heading: e.target.value })}
                placeholder="Section heading"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <textarea
                value={newSection.description}
                onChange={(e) => setNewSection({ ...newSection, description: e.target.value })}
                placeholder="Section description"
                rows="3"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                type="button"
                onClick={addSection}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium"
              >
                Add Section
              </button>
            </div>

            {/* Display Sections */}
            {policy.sections.length > 0 && (
              <div className="space-y-3">
                {policy.sections.map((section, index) => (
                  <div key={index} className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <div className="flex justify-between items-start mb-2">
                      <h4 className="font-semibold text-gray-800">{section.heading}</h4>
                      <button
                        type="button"
                        onClick={() => removeSection(index)}
                        className="text-red-600 hover:text-red-800 font-medium"
                      >
                        Remove
                      </button>
                    </div>
                    <p className="text-gray-600 text-sm whitespace-pre-wrap">{section.description}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Submit Button */}
          <div className="flex gap-4 pt-6 border-t">
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-medium"
            >
              {saving ? (
                <>
                  <FaSpinner className="animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <FaSave />
                  Save Policy
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CancellationPolicy;
